import React, { useState, useEffect } from "react";
import passive from "passive-voice";
import { syllable } from "syllable";
import { STAGE_ORDER } from "../../utils/stages";

const getReadBadge = (score) => {
  if (score >= 80) return { badge: "🟢 Strong", color: "green" };
  if (score >= 50) return { badge: "🟡 Moderate", color: "orange" };
  return { badge: "🔴 Weak", color: "red" };
};

const transitionWords = [
  "additionally", "afterward", "although", "and", "as a result", "because", "besides",
  "but", "consequently", "finally", "first", "for example", "furthermore", "hence",
  "however", "in addition", "in conclusion", "in fact", "indeed", "instead", "moreover",
  "nevertheless", "next", "nonetheless", "not only", "now", "of course", "on the other hand",
  "second", "similarly", "since", "so", "still", "then", "therefore", "thus", "to summarize",
  "while", "yet"
];

const countSentences = (text) => (text.match(/[.?!]\s/g) || []).length;
const countWords = (text) => (text.match(/\b\w+\b/g) || []).length;
const countParagraphs = (text) => text.split(/\n\s*\n/).length;
const detectPassive = (text) => passive(text);

const startsWithTransition = (sentence) => {
  const normalized = sentence.toLowerCase().trim();
  return transitionWords.some((word) =>
    normalized.startsWith(word + " ") || normalized === word
  );
};

const getTrafficLight = (score, t) => {
  if (typeof score !== "number" || Number.isNaN(score)) return "⚪";

  // Preferred: Window model (min–max range, with optional yellowMin/yellowMax buffers)
  // Example:
  // green window = min–max
  // yellow buffer = yellowMin–min or max–yellowMax
  if ("min" in t || "max" in t) {
    const min = t.min ?? -Infinity;
    const max = t.max ?? Infinity;
    const yMin = t.yellowMin ?? -Infinity;
    const yMax = t.yellowMax ?? Infinity;

    if (score >= min && score <= max) return "🟢"; // Ideal range
    if (score >= yMin && score < min) return "🟡"; // Slightly below range
    if (score > max && score <= yMax) return "🟡"; // Slightly above range
    return "🔴"; // Outside acceptable range
  }

  // Binary pass/fail (exact match)
  if ("pass" in t) return score === t.pass ? "🟢" : "🔴";

  // Legacy directional model (higher = better)
  if ("green" in t || "yellow" in t) {
    const g = t.green ?? Infinity;
    const y = t.yellow ?? -Infinity;
    if (score >= g) return "🟢";
    if (score >= y) return "🟡";
    return "🔴";
  }

  // Fallback
  return "🔴";
};

const getReadabilitySummary = (scores) => {
  const greens = scores.filter(s => s === "🟢").length;
  const yellows = scores.filter(s => s === "🟡").length;
  const reds = scores.filter(s => s === "🔴").length;

  if (reds >= 2) return { badge: "🔴 Weak", color: "red" };
  if (yellows >= 2) return { badge: "🟡 Moderate", color: "orange" };
  return { badge: "🟢 Strong", color: "green" };
};

const extractSectionsBetweenSubheadings = (html) => {
  // Split at h2/h3 and keep the content in between
  const parts = html.split(/<h[2-3][^>]*>.*?<\/h[2-3]>/gi);
  return parts.map((section) => section.replace(/<[^>]+>/g, " ").trim());
};

const ReadabilityAnalyser = ({ content, stageKey, currentUserRole }) => {
  const plainText = content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  const isWriter = currentUserRole === 2;

  const currentStageIndex = STAGE_ORDER.indexOf(stageKey);
  const revisedDraftIndex = STAGE_ORDER.indexOf("SECOND_DRAFT");

  const writerAtAllowedStage =
    revisedDraftIndex !== -1 &&
    currentStageIndex !== -1 &&
    currentStageIndex >= revisedDraftIndex;

  // Editors/Lead/Admin: always see. Writers: only from Revised Draft (SECOND_DRAFT) onward.
  const canShowReadability = !isWriter || writerAtAllowedStage;

  const [readScore, setReadScore] = useState(0);
  const [hasInput, setHasInput] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [trafficLights, setTrafficLights] = useState([]);
  const [detailedTrafficLights, setDetailedTrafficLights] = useState({});
  const [metricScores, setMetricScores] = useState({});

  const [fleschScore, setFleschScore] = useState(0);
  const [fleschKincaidGrade, setFleschKincaidGrade] = useState(0);
  const [averageSentenceLength, setAverageSentenceLength] = useState(0);
  const [longSentenceRatio, setLongSentenceRatio] = useState(0);
  const [longParagraphs, setLongParagraphs] = useState(0);
  const [paragraphCount, setParagraphCount] = useState(0);
  const [longSentences, setLongSentences] = useState(0);
  const [longParagraphRatio, setLongParagraphRatio] = useState(0);
  const [complexWords, setComplexWords] = useState(0);
  const [complexWordRatio, setComplexWordRatio] = useState(0);
  const [passiveInstances, setPassiveInstances] = useState([]);
  const [passiveRatio, setPassiveRatio] = useState(0);
  const [longBlocks, setLongBlocks] = useState(0);
  const [blockCount, setBlockCount] = useState(0);
  const [longBlockRatio, setLongBlockRatio] = useState(0);
  const [longSections, setLongSections] = useState(0);
  const [sectionCount, setSectionCount] = useState(0);
  const [longSectionRatio, setLongSectionRatio] = useState(0);
  const [sentenceList, setSentenceList] = useState([]);
  const [sentencesWithTransitions, setSentencesWithTransitions] = useState(0);
  const [transitionUsagePercent, setTransitionUsagePercent] = useState(0);
  const [estimatedReadingTime, setEstimatedReadingTime] = useState(0);

  const removeHeadings = (html) => html.replace(/<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>/gi, "\n\n");

  const stripHtml = (html) =>
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<\/(div|section|article|li)>/gi, "\n\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  const getParagraphs = (html) =>
    stripHtml(removeHeadings(html))
      .split(/\n\s*\n/)
      .map(s => s.trim())
      .filter(Boolean);

  const htmlToBlocksText = (html = "") => {
    const BREAK = "\n\n";      // hard block separator
    const SOFT = "\n";         // soft line break (won’t create a block on its own)

    return (html || "")

    // Normalize Windows/Mac line endings first
    .replace(/\r\n?/g, "\n")

      // Existing hard breaks
      .replace(/<hr\s*\/?>/gi, BREAK)
      .replace(/<figure[\s\S]*?<\/figure>/gi, BREAK)
      .replace(/<img\b[^>]*>/gi, BREAK)
      .replace(/<(iframe|video|audio|table|pre|blockquote)\b[\s\S]*?<\/\1>/gi, BREAK)

      // NEW: inline quotes / pull-quotes should split blocks
      .replace(/<q\b[^>]*>[\s\S]*?<\/q>/gi, BREAK)
      .replace(
      /<(span|div|p)\b[^>]*?(?:class=(["'])[^"']*\b(?:inline-quote|pull-quote)\b[^"']*\2|data-inline-quote\b[^>]*)[^>]*>[\s\S]*?<\/\1>/gi, BREAK)


      // Paragraph/list line breaks (single newline, so two in a row become a block)
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/li>/gi, "\n")

      // Optional: list container boundaries as soft breaks (helps giant lists)
      .replace(/<\/(ul|ol)>/gi, SOFT)
      .replace(/<br\s*\/?>/gi, SOFT)

      // Strip remaining tags/entities
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")

      // Normalize: collapse ≥3 newlines to exactly 2; trim edges
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const getBlocks = (html) =>
    htmlToBlocksText(html)
      .split(/\n{2,}/)     // 2+ newlines = block boundary
      .map(s => s.trim())
      .filter(Boolean);

  const htmlToSectionsText = (html = "") => {
    const TOK = "__SECTION_BREAK__";
    return (html || "")
      .replace(/<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>/gi, TOK)
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/li>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\n{2,}/g, "\n")
      .trim()
      .replace(new RegExp(TOK, "g"), "\n\n");
  };

  const getSections = (html) =>
    htmlToSectionsText(html)
      .split(/\n{2,}/)
      .map(s => s.trim())
      .filter(s => (s.match(/\b\w+\b/g) || []).length > 0);

  useEffect(() => {
    const stripped = stripHtml(content || "").trim();
    setHasInput(stripped !== "");
  }, [content]);

const calculateReadScore = () => {

  const blocks = getBlocks(content || "");
  const blockCountLocal = blocks.length;
  const blockWordCounts = blocks.map(p => countWords(p));

  const longBlocksLocal = blockWordCounts.filter(w => w > 250).length;
  const longBlockRatioLocal = blockCountLocal > 0
    ? (longBlocksLocal / blockCountLocal) * 100
    : 0;

  const sections = getSections(content || "");
  const sectionCountLocal = sections.length;
  const sectionWordCounts = sections.map(p => countWords(p));

  const longSectionsLocal = sectionWordCounts.filter(w => w > 300).length;
  const longSectionRatioLocal = sectionCountLocal > 0
    ? (longSectionsLocal / sectionCountLocal) * 100
    : 0;

  const paragraphs = getParagraphs(content || "");
  const wordList = plainText.match(/\b\w+\b/g) || [];

  const totalWords = wordList.length;
  const totalSentences = countSentences(plainText);
  const totalParagraphs = paragraphs.length;

  const syllableCount = wordList.reduce((sum, word) => sum + syllable(word), 0);

  const fleschScore = totalSentences > 0 && totalWords > 0
    ? 206.835 - 1.015 * (totalWords / totalSentences) - 84.6 * (syllableCount / totalWords)
    : 0;
  const fleschKincaidGrade = totalSentences > 0 && totalWords > 0
    ? 0.39 * (totalWords / totalSentences) + 11.8 * (syllableCount / totalWords) - 15.59
    : 0;

  const averageSentenceLength = totalSentences > 0 ? totalWords / totalSentences : 0;
  const longSentences = plainText.split(/[.?!]\s/).filter((s) => s.split(" ").length > 20).length;
  const longSentenceRatio = (longSentences / totalSentences) * 100;
  const longParagraphs = paragraphs.filter((p) => countWords(p) > 150).length;

  const longParagraphRatio = (longParagraphs / totalParagraphs) * 100;

  const complexWords = wordList.filter(word => syllable(word) >= 3).length;
  const complexWordRatio = totalWords > 0 ? (complexWords / totalWords) * 100 : 0;

  const passiveInstances = detectPassive(plainText);
  const passiveRatio = passiveInstances.length / (totalSentences || 1);

  const sentenceList = plainText.split(/[.?!]\s/).map(s => s.trim()).filter(Boolean);
  const sentencesWithTransitions = sentenceList.filter(startsWithTransition).length;
  const transitionUsagePercent = (sentencesWithTransitions / (sentenceList.length || 1)) * 100;

  const estimatedReadingTime = totalWords > 0 ? Math.ceil(totalWords / 225) : 0;

  setFleschScore(fleschScore);
  setFleschKincaidGrade(fleschKincaidGrade);
  setAverageSentenceLength(averageSentenceLength);
  setLongSentences(longSentences);
  setParagraphCount(totalParagraphs);
  setLongParagraphs(longParagraphs);
  setLongSentenceRatio(longSentenceRatio);
  setLongParagraphRatio(longParagraphRatio);
  setComplexWords(complexWords);
  setComplexWordRatio(complexWordRatio);
  setPassiveInstances(passiveInstances);
  setPassiveRatio(passiveRatio);
  setLongBlocks(longBlocksLocal);
  setBlockCount(blockCountLocal);
  setLongBlockRatio(longBlockRatioLocal);
  setLongSections(longSectionsLocal);
  setSectionCount(sectionCountLocal);
  setLongSectionRatio(longSectionRatioLocal);
  setSentenceList(sentenceList);
  setSentencesWithTransitions(sentencesWithTransitions);
  setTransitionUsagePercent(transitionUsagePercent);
  setEstimatedReadingTime(estimatedReadingTime);

  let score = 0;
  const maxScore = 100;
  const suggestions = [];
  const metricScores = {}; // key: metric name, value: { score: X, max: Y }

  if (fleschScore >= 60) {
    score += 10;
    metricScores.fleschTL = { score: 10, max: 10 };
  } else {
    metricScores.fleschTL = { score: 0, max: 10 };
    suggestions.push("Simplify wording for better readability.");
  }

  if (fleschKincaidGrade <= 8) {
    score += 10;
    metricScores.fkGradeTL = { score: 10, max: 10 };
  } else {
    metricScores.fkGradeTL = { score: 0, max: 10 };
    suggestions.push("Lower the grade level by using shorter sentences and simpler words.");
  }

  if (averageSentenceLength > 20 || averageSentenceLength < 10) {
    metricScores.avgSentenceLengthTL = { score: 0, max: 10 };
    if (averageSentenceLength < 10) {
      suggestions.push("Combine short sentences to improve flow and readability.");
    } else {
      suggestions.push("Break up long sentences to reduce complexity and improve clarity.");
    }
  } else if (averageSentenceLength > 16 || averageSentenceLength < 12) {
    score += 5;
    metricScores.avgSentenceLengthTL = { score: 5, max: 10 };
    if (averageSentenceLength < 12) {
      suggestions.push("Combine short sentences to improve flow and readability.");
    } else {
      suggestions.push("Break up long sentences to reduce complexity and improve clarity.");
    }
  } else {
    score += 10;
    metricScores.avgSentenceLengthTL = { score: 10, max: 10 };
  }

  if (longSentenceRatio < 25) {
    score += 10;
    metricScores.sentenceTL = { score: 10, max: 10 };
  } else {
    metricScores.sentenceTL = { score: 0, max: 10 };
    suggestions.push("Split long sentences (>20 words).");
  }

  if (longParagraphRatio < 20) {
    score += 10;
    metricScores.longParagraphTL = { score: 10, max: 10 };
  } else if (longParagraphRatio < 30) {
    score += 5;
    metricScores.longParagraphTL = { score: 5, max: 10 };
    suggestions.push("Break up long paragraphs.");
  } else {
    metricScores.longParagraphTL = { score: 0, max: 10 };
    suggestions.push("Break up long paragraphs.");
  }

  if (complexWordRatio <= 10) {
    score += 10;
    metricScores.complexWordRatioTL = { score: 10, max: 10 };
  } else {
    metricScores.complexWordRatioTL = { score: 0, max: 10 };
    suggestions.push("Use simpler vocabulary to reduce complex word usage.");
  }

  if (passiveRatio <= 0.2) {
    score += 10;
    metricScores.passiveTL = { score: 10, max: 10 };
  } else {
    metricScores.passiveTL = { score: 0, max: 10 };
    suggestions.push("Use more active voice.");
  }

  if (longBlockRatioLocal < 5) {
    score += 10;
    metricScores.longBlocksTL = { score: 10, max: 10 };
  } else if (longBlockRatioLocal < 10) {
    score += 5;
    metricScores.longBlocksTL = { score: 5, max: 10 };
    suggestions.push("Add visual separators to reduce large, uninterrupted blocks of text.");
  } else {
    metricScores.longBlocksTL = { score: 0, max: 10 };
    suggestions.push("Add visual separators to reduce large, uninterrupted blocks of text.");
  }

  if (longSectionRatioLocal < 5) {
    score += 10;
    metricScores.longSectionTL = { score: 10, max: 10 };
  } else if (longSectionRatioLocal < 10) {
    score += 5;
    metricScores.longSectionTL = { score: 5, max: 10 };
    suggestions.push("Add headings and/or subheadings to break up long sections.");
  } else {
    metricScores.longSectionTL = { score: 0, max: 10 };
    suggestions.push("Add headings and/or subheadings to break up long sections.");
  }

  if (transitionUsagePercent < 10 || transitionUsagePercent > 30) {
    metricScores.transitionTL = { score: 0, max: 10 };
    if (transitionUsagePercent < 10) {
      suggestions.push("Add more transition words to improve flow.");
    } else {
      suggestions.push("Reduce transition words; vary sentence openings to improve rhythm and flow.");
    }
  } else if (transitionUsagePercent < 15 || transitionUsagePercent > 25) {
    score += 5;
    metricScores.transitionTL = { score: 5, max: 10 };
    if (transitionUsagePercent < 15) {
      suggestions.push("Add more transition words to improve flow.");
    } else {
      suggestions.push("Reduce transition words; vary sentence openings to improve rhythm and flow.");
    }
  } else {
    score += 10;
    metricScores.transitionTL = { score: 10, max: 10 };
  }

  setReadScore(Math.round(Math.max(0, Math.min(score, maxScore))));

  setSuggestions(suggestions);
  setMetricScores(metricScores);

  const thresholds = {
    // Comprehension
    fleschTL: { min: 60, max: 80, yellowMin: 50, yellowMax: 90 },
    fkGradeTL: { min: 5,  max: 8,  yellowMin: 4,  yellowMax: 9 },

    // Structure
    avgSentenceLengthTL: { min: 12, max: 16, yellowMin: 10, yellowMax: 20 },
    sentenceTL: { min: 0, max: 25, yellowMax: 35 }, // Long sentences %
    longParagraphTL: { min: 0, max: 20, yellowMax: 30 }, // Long paragraphs %

    // Clarity
    complexWordRatioTL: { min: 0,  max: 10, yellowMax: 15 },
    passiveTL: { min: 0,  max: 20, yellowMax: 30 }, // Passive ratio %

    // Flow & Formatting
    longBlocksTL: { min: 0, max: 5, yellowMax: 10 }, // Long blocks %
    longSectionTL: { min: 0, max: 5, yellowMax: 10 }, // Long sections %
    transitionTL: { min: 15, max: 25, yellowMin: 10, yellowMax: 30 },
  };

  const inputValues = {
    fleschTL: fleschScore,
    fkGradeTL: fleschKincaidGrade,
    avgSentenceLengthTL: averageSentenceLength,
    sentenceTL: Number(longSentenceRatio),
    longParagraphTL: Number(longParagraphRatio),
    complexWordRatioTL: complexWordRatio,
    passiveTL: Number(passiveRatio * 100),
    longBlocksTL: Number(longBlockRatioLocal),
    longSectionTL: Number(longSectionRatioLocal),
    transitionTL: transitionUsagePercent,
  }

  const readTrafficLights = Object.fromEntries(
    Object.entries(inputValues).map(([key, value]) => [
      key,
      getTrafficLight(value, thresholds[key]),
    ])
  );

  setDetailedTrafficLights(readTrafficLights);
  setTrafficLights(Object.values(readTrafficLights));
}

  useEffect(() => {
    calculateReadScore();
  }, [content]);

  const summary = getReadBadge(readScore);

  const styles = {
    sectionHeader: {
      fontWeight: "bold",
      fontStyle: "italic",
      margin: "16px 0 8px",
      paddingTop: "8px",
      borderTop: "1px solid #e5e5e5",
      color: "#111"
    },
    list: { listStyle: "none", paddingLeft: 0, margin: 0 },
    metricRow: { display: "flex", gap: "10px", marginBottom: "10px", alignItems: "flex-start" },
    icon: { minWidth: 24, textAlign: "center", fontSize: "1.1rem", lineHeight: "1.2rem" },
    content: { lineHeight: 1.25 },
    titleLine: { fontWeight: "600" },
    metaLine: { color: "#666", fontSize: "0.85rem", marginTop: 2 }
  };

  const MetricRow = ({ light, title, value, meta }) => (
    <li style={styles.metricRow}>
      <span style={styles.icon}>{light ?? "⚪"}</span>
      <div style={styles.content}>
        <div style={styles.titleLine}><strong>{title}:</strong> {value}</div>
        {meta && <div style={styles.metaLine}>{meta}</div>}
      </div>
    </li>
  );

  return (
    <div className="readability-analysis">
      <div style={{ marginTop: "10px", marginBottom: "10px", fontWeight: "bold", fontSize: "16px", color: "#000" }}>
        Readability Score:{" "}
        {!hasInput ? (
          <span
            style={{
              fontStyle: "italic",
              fontSize: "16px",
              fontWeight: "normal",
              color: "#555",
            }}
          >
            Start adding content to see your Readability score.
          </span>
        ) : !canShowReadability ? (
          <span
            style={{
              fontStyle: "italic",
              fontSize: "16px",
              fontWeight: "normal",
              color: "#555",
            }}
          >
            Your Readability score will be displayed once your draft reaches the
            Revised Draft stage.
          </span>
        ) : (
          <span style={{ color: summary.color }}>
            {summary.badge} ({readScore}%)
          </span>
        )}
      </div>
      {hasInput && canShowReadability && (
        <>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "12px",
              background: "#fafafa",
              border: "1px solid #ddd",
              borderRadius: "6px",
              padding: "6px 10px",
              margin: "4px 0 14px 0",
              fontWeight: "500",
              fontSize: "0.9rem",
            }}
          >
            <span style={{ fontWeight: "700" }}>At a Glance:</span>
            <span>🟢 {Object.values(detailedTrafficLights).filter(v => v === "🟢").length}</span>
            <span>🟡 {Object.values(detailedTrafficLights).filter(v => v === "🟡").length}</span>
            <span>🔴 {Object.values(detailedTrafficLights).filter(v => v === "🔴").length}</span>
          </div>
          <li style={{ listStyleType: "none", fontWeight: "bold", margin: "10px 0 4px" }}>Scorecard:</li>
          <ul style={styles.list}>

            <li style={styles.sectionHeader}>Comprehension</li>
            <MetricRow
              light={detailedTrafficLights.fleschTL ?? "⚪"}
              title="Flesch Reading Ease"
              value={Math.round(fleschScore)}
              meta={`Score ${metricScores.fleschTL?.score ?? 0}/${metricScores.fleschTL?.max ?? 10} • Ideal: 60–80 • Scale from 0–100 (Higher = Easier to read)`}
            />
            <MetricRow
              light={detailedTrafficLights.fkGradeTL ?? "⚪"}
              title="Flesch–Kincaid Grade Level"
              value={fleschKincaidGrade.toFixed(1)}
              meta={`Score ${metricScores.fkGradeTL?.score ?? 0}/${metricScores.fkGradeTL?.max ?? 10} • Ideal: 5–8 • School grade level (Lower = Easier to read)`}
            />

            <li style={styles.sectionHeader}>Structure</li>
            <MetricRow
              light={detailedTrafficLights.avgSentenceLengthTL ?? "⚪"}
              title="Average Sentence Length"
              value={`${averageSentenceLength.toFixed(1)} words`}
              meta={`Score ${metricScores.avgSentenceLengthTL?.score ?? 0}/${metricScores.avgSentenceLengthTL?.max ?? 10} • Ideal: 12-16 words`}
            />
            <MetricRow
              light={detailedTrafficLights.sentenceTL ?? "⚪"}
              title="Long Sentences (>20 words)"
              value={`${longSentences} of ${sentenceList.length} sentences (${longSentenceRatio.toFixed(1)}%)`}
              meta={`Score ${metricScores.sentenceTL?.score ?? 0}/${metricScores.sentenceTL?.max ?? 10} • Ideal: 0-25%`}
            />
            <MetricRow
              light={detailedTrafficLights.longParagraphTL ?? "⚪"}
              title="Long Paragraphs (>150 words)"
              value={`${longParagraphs} of ${paragraphCount} paragraphs (${longParagraphRatio.toFixed(1)}%)`}
              meta={`Score ${metricScores.longParagraphTL?.score ?? 0}/${metricScores.longParagraphTL?.max ?? 10} • Ideal: 0-20%`}
            />

            <li style={styles.sectionHeader}>Clarity</li>
            <MetricRow
              light={detailedTrafficLights.complexWordRatioTL ?? "⚪"}
              title="Complex Words (≥3 syllables)"
              value={`${complexWords} of ${countWords(plainText)} words (${complexWordRatio.toFixed(1)}%)`}
              meta={`Score ${metricScores.complexWordRatioTL?.score ?? 0}/${metricScores.complexWordRatioTL?.max ?? 10} • Ideal: 0-10%`}
            />
            <MetricRow
              light={detailedTrafficLights.passiveTL ?? "⚪"}
              title="Passive Voice"
              value={`${passiveInstances.length} of ${sentenceList.length} sentences (${(passiveRatio * 100).toFixed(1)}%)`}
              meta={`Score ${metricScores.passiveTL?.score ?? 0}/${metricScores.passiveTL?.max ?? 10} • Ideal: 0-20%`}
            />

            <li style={styles.sectionHeader}>Flow & Formatting</li>
            <MetricRow
              light={detailedTrafficLights.longBlocksTL ?? "⚪"}
              title="Long blocks without breaks (>250 words)"
              value={`${longBlocks} of ${blockCount} blocks (${longBlockRatio.toFixed(1)}%)`}
              meta={`Score ${metricScores.longBlocksTL?.score ?? 0}/${metricScores.longBlocksTL?.max ?? 10} • Ideal: 0-5%`}
            />
            <MetricRow
              light={detailedTrafficLights.longSectionTL ?? "⚪"}
              title="Long sections without headings (>300 words)"
              value={`${longSections} of ${sectionCount} sections (${longSectionRatio.toFixed(1)}%)`}
              meta={`Score ${metricScores.longSectionTL?.score ?? 0}/${metricScores.longSectionTL?.max ?? 10} • Ideal: 0-5%`}
            />
            <MetricRow
              light={detailedTrafficLights.transitionTL ?? "⚪"}
              title="Sentences starting with transition words"
              value={`${sentencesWithTransitions} of ${sentenceList.length} sentences (${transitionUsagePercent.toFixed(1)}%)`}
              meta={`Score ${metricScores.transitionTL?.score ?? 0}/${metricScores.transitionTL?.max ?? 10} • Ideal: 15–25%`}
            />

            <li style={styles.sectionHeader}>Estimated Reading Time</li>
            <li style={{ ...styles.metricRow, marginTop: 12 }}>
              <span style={styles.icon}>⏱</span>
              <div style={styles.content}>
                <div style={styles.titleLine}> {estimatedReadingTime} minute read</div>
              </div>
            </li>
            {suggestions.length > 0 && (
              <>
                <li style={styles.sectionHeader}>Suggestions</li>
                  {suggestions.map((s, i) => (
                    <li key={i} style={{ ...styles.metricRow, marginTop: 12 }}>
                      <span style={styles.icon}>💡</span>
                      <div>{s}</div>
                    </li>
                  ))}
              </>
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default ReadabilityAnalyser;
